// ============================================================
// TMR Portal Admin — o campo do valor de uma regra ↔ o corpo da escrita (25)
// ============================================================
import type { RuleValue, RuleValueType, ScoreBandValue } from './contract';

/** What the field read: a value to send, or the reason it cannot be sent. */
export type RuleValueParse = { ok: true; value: RuleValue } | { ok: false; error: string };

/** What a rule accepts — the parts of it the field checks against. */
export interface RuleValueShape {
  valueType: RuleValueType;
  minimum: number | null;
  maximum: number | null;
  options: string[];
}

/**
 * Reads what the operator typed or chose for a rule's value.
 *
 * It checks the kind and what the rule accepts, as the server will, so the operator learns before a round
 * trip — and it does not replace the server, which refuses the same values with
 * `business_rules.invalid_value` (`25` §5).
 *
 * - **Integer:** only whole numbers pass. `3,5` and `3.5` are refused rather than rounded, because rounding
 *   would send a value the operator did not type.
 * - **Choice:** only one of the options, exactly as the API sent it.
 *
 * @param input The field's text, or the chosen option.
 * @param rule What the rule accepts.
 */
export function parseRuleValue(input: string, rule: RuleValueShape): RuleValueParse {
  if (rule.valueType === 'Choice') {
    return rule.options.includes(input) ? { ok: true, value: input } : { ok: false, error: 'Escolha uma das opções.' };
  }

  const text = input.trim();
  if (text === '') return { ok: false, error: 'Informe um valor.' };
  if (!/^-?\d+$/.test(text)) return { ok: false, error: 'Informe um número inteiro.' };

  const value = Number(text);
  if ((rule.minimum !== null && value < rule.minimum) || (rule.maximum !== null && value > rule.maximum)) {
    return { ok: false, error: `Use um valor de ${rule.minimum} a ${rule.maximum}.` };
  }

  return { ok: true, value };
}

/** One band as the operator is editing it: the ends as typed, the action as chosen. */
export interface ScoreBandDraft {
  de: string;
  ate: string;
  acao: string;
}

/** The most bands the server accepts (`BusinessRuleDefinition.MaximumBands`). */
export const MAXIMUM_SCORE_BANDS = 20;

/**
 * Reads the bands the operator edited for a `ScoreBands` rule, with the checks the server makes — and in
 * the same order, so the message is the one the server would give (`26` §5):
 *
 * - every end a whole number, every action one of the options;
 * - no band ending before it starts;
 * - in order, the bands cover the rule's range — `minimum` to `maximum` — with no gap and no overlap.
 *
 * The bands go out ordered by where each starts, which is also how the server stores them.
 *
 * @param drafts The bands as edited, in any order.
 * @param rule What the rule accepts.
 */
export function parseScoreBands(drafts: ScoreBandDraft[], rule: RuleValueShape): RuleValueParse {
  if (drafts.length === 0) return { ok: false, error: `Informe ao menos uma faixa, de ${rule.minimum} a ${rule.maximum}.` };
  if (drafts.length > MAXIMUM_SCORE_BANDS) return { ok: false, error: `Use no máximo ${MAXIMUM_SCORE_BANDS} faixas.` };

  const bands: ScoreBandValue[] = [];
  for (const draft of drafts) {
    const de = draft.de.trim();
    const ate = draft.ate.trim();
    if (!/^\d+$/.test(de) || !/^\d+$/.test(ate)) {
      return { ok: false, error: 'Informe o início e o fim de cada faixa como números inteiros.' };
    }
    if (!rule.options.includes(draft.acao)) return { ok: false, error: 'Escolha uma das ações em cada faixa.' };
    bands.push({ de: Number(de), ate: Number(ate), acao: draft.acao });
  }

  if (bands.some((band) => band.de > band.ate)) {
    return { ok: false, error: 'Em cada faixa, o início não pode passar do fim.' };
  }

  const ordered = [...bands].sort((a, b) => a.de - b.de);
  const covers = ordered[0].de === rule.minimum
    && ordered[ordered.length - 1].ate === rule.maximum
    && ordered.every((band, i) => i === 0 || band.de === ordered[i - 1].ate + 1);

  return covers
    ? { ok: true, value: ordered }
    : { ok: false, error: `As faixas precisam cobrir de ${rule.minimum} a ${rule.maximum}, sem buraco nem sobreposição.` };
}

/**
 * The bands of a stored value as the editor starts them, in the order the scores run.
 *
 * @param value The value in force; anything but a list starts the editor with no band.
 */
export function scoreBandDrafts(value: RuleValue): ScoreBandDraft[] {
  if (!Array.isArray(value)) return [];
  return [...value]
    .sort((a, b) => a.de - b.de)
    .map((band) => ({ de: String(band.de), ate: String(band.ate), acao: band.acao }));
}
